/**
 * ReviewHub (ADR-0023, mockup v2 approved 2026-10-28): the quarter at a glance.
 * One row per client review: where it is in the five-stage cycle, the seven health areas,
 * the three gates and open commitments. Follows the company switcher; columns sort.
 */
import { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api, type ChClient, type RhListItem } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { fmtDT } from "../../lib/format";
import { Loading, Tile } from "../../components/bits";
import { timeOf, useSort } from "../../components/Sort";
import { Icon } from "../../components/Icon";
import { AREAS, RATING_WORD, STAGES, STAGE_LABEL, type Rating } from "../../../../shared/reviewhub";
import { SeeHow } from "../../components/SeeHow";

export const RATING_CLS: Record<Rating, string> = { green: "g", yellow: "y", red: "r", incomplete: "i", na: "n", developing: "d" };
export function RatingPill({ r, word }: { r: Rating; word?: string }) {
  return <span className={`rh-p rh-${RATING_CLS[r]}`}>{word ?? RATING_WORD[r]}</span>;
}
export function HealthSquares({ ratings }: { ratings: { area: string; rating: Rating }[] }) {
  const n = (k: Rating) => ratings.filter((x) => x.rating === k).length;
  const words = [n("red") && `${n("red")} action`, n("yellow") && `${n("yellow")} attention`, n("green") && `${n("green")} healthy`, n("incomplete") && `${n("incomplete")} not measured`].filter(Boolean);
  return (
    <div>
      <div className="rh-sq" aria-hidden="true">{ratings.map((x) => <i key={x.area} className={`rh-${RATING_CLS[x.rating]}`} title={`${AREAS.find((a) => a.key === x.area)?.name}: ${RATING_WORD[x.rating]}`} />)}</div>
      <div className="cap rh-cap">{words.join(" · ") || "Nothing rated yet"}</div>
    </div>
  );
}
export function StageMini({ stage }: { stage: string }) {
  const i = STAGES.indexOf(stage as (typeof STAGES)[number]);
  return (
    <div>
      <div className="rh-sm"><b>{STAGE_LABEL[stage as (typeof STAGES)[number]] ?? stage}</b><span className="cap rh-cap">step {i + 1} of 5</span></div>
      <div className="rh-seg5" aria-hidden="true">{STAGES.map((s, n) => <i key={s} className={n < i ? "done" : n === i ? "on" : ""} />)}</div>
    </div>
  );
}
export function Gate({ pass, text, wait }: { pass: boolean; text: string; wait?: boolean }) {
  return <span className={`rh-gate ${pass ? "ok" : wait ? "wait" : "bad"}`}>{text}</span>;
}

export function ReviewsPage() {
  const { p, ws, toast, me } = useStore();
  const nav = useNavigate();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.rhReviews>> | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [quarter, setQuarter] = useState<string>("");
  const [q, setQ] = useState("");
  const [chip, setChip] = useState<"all" | "mine" | "gates" | "notbooked">("all");
  const [starting, setStarting] = useState(false);
  const codes = p.rhCodes.filter((c) => ws === "ALL" || c === ws);
  const load = () => api.rhReviews(ws, quarter || undefined).then(setData, (e: Error) => setErr(e.message));
  useEffect(() => { setData(null); setErr(null); if (codes.length) void load(); }, [ws, quarter]);
  const rows = data?.reviews ?? [];
  const { sorted, th } = useSort("rh.reviews", rows, {
    client: (r) => r.client_name, stage: (r) => STAGES.indexOf(r.stage), meeting: (r) => timeOf(r.meeting_at),
    health: (r) => r.ratings.filter((x) => x.rating === "red").length * 10 + r.ratings.filter((x) => x.rating === "yellow").length,
    commitments: (r) => r.commitments_open, company: (r) => r.company,
  }, { key: "meeting", dir: "asc" });
  const now = Date.now();
  const filtered = useMemo(() => sorted.filter((r) => {
    if (q && !`${r.client_name} ${r.client_code} ${r.owner_name ?? ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (chip === "mine") return r.owner_user_id === me.user.id || r.engineer_user_id === me.user.id || r.pitch.requested_user_id === me.user.id;
    if (chip === "gates") return STAGES.indexOf(r.stage) < 2 && !r.gates.ready && !!r.meeting_at;
    if (chip === "notbooked") return !r.meeting_at && STAGES.indexOf(r.stage) < 2;
    return true;
  }), [sorted, q, chip]);

  if (!p.rhCodes.length) return <Navigate to="/" replace />;
  if (!codes.length) return <div className="card empty view"><h3>ReviewHub isn't on for {coName(ws)}</h3>Switch to a company that has it, or ask a platform admin to turn it on in Settings &gt; ReviewHub.</div>;
  if (err) return <div className="card empty view"><h3>Reviews didn't load</h3>{err}</div>;
  if (!data) return <Loading />;
  const editCodes = codes.filter((c) => p.rh(c).edit);
  const delivered = rows.filter((r) => r.delivered_at).length;
  const booked = rows.filter((r) => r.meeting_at && !r.delivered_at).length;
  const notBooked = rows.filter((r) => !r.meeting_at && STAGES.indexOf(r.stage) < 2).length;
  const open = rows.reduce((n, r) => n + r.commitments_open, 0);
  const late = rows.reduce((n, r) => n + r.commitments_late, 0);
  const mine = rows.filter((r) => r.owner_user_id === me.user.id || r.engineer_user_id === me.user.id || r.pitch.requested_user_id === me.user.id).length;
  const failing = rows.filter((r) => STAGES.indexOf(r.stage) < 2 && !r.gates.ready && !!r.meeting_at).length;
  const qLabel = data.quarter === "all" ? "All quarters" : data.quarter;

  return (
    <div className="view">
      <div className="hgroup">
        <div>
          <div className="eyebrow">ReviewHub · {ws === "ALL" ? "All my companies" : coName(ws)}</div>
          <h1 className="title">Reviews</h1>
          <div className="lede">Quarterly technology business reviews. {qLabel}: {rows.length} review{rows.length === 1 ? "" : "s"}, {delivered} delivered, {booked} booked, {notBooked} not booked.</div>
        </div>
        <div className="row-btns">
          <div className="seg">
            {data.quarters.slice(0, 3).map((x) => <button key={x} className={data.quarter === x ? "on" : ""} onClick={() => setQuarter(x)}>{x}{x === data.current ? " (now)" : ""}</button>)}
            <button className={data.quarter === "all" ? "on" : ""} onClick={() => setQuarter("all")}>All quarters</button>
          </div>
          <SeeHow video="quarterly-review" />
          {editCodes.length > 0 && <button className="btn btn-accent" onClick={() => setStarting((s) => !s)}><Icon name="plus" />Start a review</button>}
        </div>
      </div>

      {starting && <StartReview codes={editCodes} quarter={data.current} onDone={(id) => { setStarting(false); toast("Review started. Book the meeting next."); nav(`/reviewhub/r/${id}?tab=details`); }} onCancel={() => setStarting(false)} />}

      <div className="kpis k5">
        <div className="kc" style={{ ["--kc" as string]: "#2F7A52", cursor: "default" }}><span className="ic"><Icon name="ok" /></span><span className="v">{delivered}</span><span className="l">Delivered</span></div>
        <div className="kc" style={{ ["--kc" as string]: "#2F6FB3", cursor: "default" }}><span className="ic"><Icon name="cal" /></span><span className="v">{booked}</span><span className="l">Meetings booked</span></div>
        <button type="button" className="kc kc-btn" style={{ ["--kc" as string]: "#C2410C" }} onClick={() => setChip("notbooked")}><span className="ic"><Icon name="clock" /></span><span className="v">{notBooked}</span><span className="l">Not booked yet</span></button>
        <div className="kc" style={{ ["--kc" as string]: "#4A5462", cursor: "default" }}><span className="ic"><Icon name="tasks" /></span><span className="v">{open}</span><span className="l">Open commitments</span></div>
        <div className="kc" style={{ ["--kc" as string]: "#AE2A20", cursor: "default" }}><span className="ic"><Icon name="flag" /></span><span className="v">{late}</span><span className="l">Commitments late</span></div>
      </div>

      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} /><input placeholder="Find a client or owner" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a review" /></label>
        <div className="rh-chips" role="group" aria-label="Show">
          {([["all", `All ${rows.length}`], ["mine", `Needs me ${mine}`], ["gates", `Gates failing ${failing}`], ["notbooked", `Not booked ${notBooked}`]] as const).map(([k, l]) =>
            <button key={k} className={`rh-chip ${chip === k ? "on" : ""}`} aria-pressed={chip === k} onClick={() => setChip(k)}>{l}</button>)}
        </div>
        <span className="cap">{filtered.length} of {rows.length}</span>
      </div>

      {rows.length === 0 ? (
        <div className="card empty"><h3>No reviews {data.quarter === "all" ? "yet" : `for ${data.quarter}`}</h3>{editCodes.length ? "Start one with Start a review. After a client's first review, the next quarter's starts itself." : "A ReviewHub editor or admin starts reviews."}</div>
      ) : (
        <div className="card tbl-wrap">
          <table className="tbl rh-list">
            <thead><tr>{th("client", "Client")}{codes.length > 1 && th("company", "Company", "hide-md")}{th("stage", "Stage")}{th("meeting", "Meeting")}{th("health", "Health (7 areas)")}<th>Gates</th>{th("commitments", "Open commitments", "hide-md")}<th><span className="sr">Open</span></th></tr></thead>
            <tbody>
              {filtered.map((r) => {
                const pre = STAGES.indexOf(r.stage) < 2;
                const started = r.ratings.some((x) => x.rating !== "incomplete" && x.rating !== "developing");
                return (
                  <tr key={r.id} className="click" onClick={() => nav(`/reviewhub/r/${r.id}`)}>
                    <td><b>{r.client_name}</b><div className="cap rh-cap">{r.quarter}{r.owner_name ? ` · ${r.owner_name}` : ""}</div></td>
                    {codes.length > 1 && <td className="hide-md"><Tile code={r.company} /></td>}
                    <td><StageMini stage={r.stage} /></td>
                    <td>{r.delivered_at ? <>Delivered<div className="cap rh-cap">{fmtDT(r.meeting_at)}</div></> : r.meeting_at ? fmtDT(r.meeting_at) : <span className="rh-warn">Not booked</span>}</td>
                    <td>{started || !r.first_review ? <HealthSquares ratings={r.ratings} /> : <span className="cap rh-cap">First review: nothing rated yet</span>}</td>
                    <td>{pre ? (
                      <div className="rh-g3">
                        <div><small>Readiness</small><Gate pass={r.gates.readiness.pass} text={r.gates.readiness.text} /></div>
                        <div><small>No-sell</small><Gate pass={r.gates.nosell.pass} text={r.gates.nosell.text} wait={!r.gates.nosell.total} /></div>
                        <div><small>Pitch check</small><Gate pass={r.gates.pitch.pass} text={r.gates.pitch.text} wait={r.gates.pitch.state === "none"} /></div>
                      </div>
                    ) : <span className="rh-gate ok">Passed</span>}</td>
                    <td className="hide-md num">{r.commitments_open}{r.commitments_late > 0 && <span className="pill bad sm" style={{ marginLeft: 6 }}>{r.commitments_late} late</span>}</td>
                    <td><button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); nav(`/reviewhub/r/${r.id}`); }}>{!r.meeting_at ? "Book" : pre ? "Continue" : r.stage === "hold" ? "Open" : "Close out"}</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="foot-note">The three gates come from the TBR process: Readiness and No-sell calculate themselves; the Pitch check is a colleague's read-through. A review can't move to the meeting until all three pass.</div>
    </div>
  );
}

function StartReview({ codes, quarter, onDone, onCancel }: { codes: string[]; quarter: string; onDone: (id: string) => void; onCancel: () => void }) {
  const { toast } = useStore();
  const [company, setCompany] = useState(codes[0]!);
  const [clients, setClients] = useState<ChClient[] | null>(null);
  const [client, setClient] = useState("");
  const [qq, setQq] = useState(quarter);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setClients(null); setClient(""); api.chClients(company).then((r) => setClients(r.clients.filter((c) => c.status !== "former")), (e: Error) => toast(e.message)); }, [company]);
  const save = async () => {
    setBusy(true);
    try { onDone((await api.rhStart({ company, client_id: client, quarter: qq })).id); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="card sect" style={{ marginBottom: 16 }}>
      <h3>Start a review</h3>
      <div className="form-row">
        {codes.length > 1 && <label><span className="lbl">Company</span><select className="inp" value={company} onChange={(e) => setCompany(e.target.value)}>{codes.map((c) => <option key={c} value={c}>{coName(c)}</option>)}</select></label>}
        <label className="grow"><span className="lbl">Client (from TechHub)</span>
          <select className="inp" value={client} onChange={(e) => setClient(e.target.value)} disabled={!clients}>
            <option value="">{clients ? "Pick a client" : "Loading…"}</option>
            {(clients ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></label>
        <label><span className="lbl">Quarter</span><input className="inp mono" value={qq} onChange={(e) => setQq(e.target.value.toUpperCase())} style={{ width: 110 }} /></label>
      </div>
      <div className="row-btns"><button className="btn btn-accent" disabled={!client || !/^FY\d{2}-Q[1-4]$/.test(qq) || busy} onClick={() => void save()}>Start review</button><button className="btn btn-ghost" onClick={onCancel}>Cancel</button></div>
      <div className="help">Quarters follow the fiscal year in Settings &gt; ReviewHub (October start: FY27-Q1 is October to December 2026). After the first review, the client's next one starts itself each quarter.</div>
    </div>
  );
}
